import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { useRouter } from "../router";
import { api, ApiError, descargarArchivo } from "../api";
import { useAuth } from "../auth/AuthContext";

type Nota = {
  id: number; tipo: "credito" | "debito"; numero: string; documentoOrigen: string; documentoId: number;
  clienteId: number | null; clienteNombre: string | null; concepto: string; motivo: string | null;
  fecha: string; baseAjuste: number; ivaAjuste: number; valorTotal: number; comprobanteId: number | null;
  cude: string; generadoEn: string;
};
type Concepto = { codigo: string; nombre: string };
type Factura = { id: number; numero: string; clienteNombre: string; valorTotal: number };
type VentaPos = { id: number; numero: string; clienteNombre: string; valorTotal: number; estado: string };

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const ORIGEN_LABEL: Record<string, string> = { factura_venta: "Factura (Comercial)", venta_pos: "Venta de mostrador (POS)" };

export function Notas() {
  const { tienePermiso } = useAuth();
  const { path } = useRouter();
  const puedeCrear = tienePermiso("notas:crear");

  const [notas, setNotas] = useState<Nota[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [modalNota, setModalNota] = useState<{ documentoOrigen?: string; documentoId?: number } | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      setNotas(await api.get("/notas"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la lista de notas.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  // Si se llega desde POS ("Nota" en una venta) con ?documentoOrigen=&documentoId=,
  // abrir directamente el formulario de nueva nota pre-diligenciado.
  useEffect(() => {
    const [, query] = path.split("?");
    if (!query) return;
    const params = new URLSearchParams(query);
    const documentoOrigen = params.get("documentoOrigen") || undefined;
    const documentoIdStr = params.get("documentoId");
    if (documentoOrigen && documentoIdStr) {
      setModalNota({ documentoOrigen, documentoId: Number(documentoIdStr) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell titulo="Notas crédito / débito" breadcrumb="Notas">
      <p className="text-sm text-textmuted mb-4">
        Ajustan una factura de Comercial o una venta de mostrador de POS ya emitida — devolución, anulación,
        descuento o ajuste de precio (nota crédito); intereses, gastos de cobro o cambio de valor (nota débito).
        Cada nota genera su propio comprobante contable balanceado, ajusta la cartera pendiente cuando aplica, y
        queda neteada automáticamente en la información exógena y las declaraciones de IVA e ICA del período. No
        ajusta las retenciones ya practicadas sobre el documento original — eso requiere gestión manual con el
        contador.
      </p>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}

      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Notas emitidas</h3>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalNota({})}>+ Nueva nota</button>}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">N°</th><th className="table-th">Tipo</th><th className="table-th">Documento origen</th>
            <th className="table-th">Cliente</th><th className="table-th">Concepto</th><th className="table-th">Base</th>
            <th className="table-th">Total</th><th className="table-th">Fecha</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {notas.map((n) => (
              <tr key={n.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{n.numero}</td>
                <td className="table-td">
                  <span className={`badge ${n.tipo === "credito" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>
                    {n.tipo === "credito" ? "Nota crédito" : "Nota débito"}
                  </span>
                </td>
                <td className="table-td text-xs">{ORIGEN_LABEL[n.documentoOrigen] || n.documentoOrigen} #{n.documentoId}</td>
                <td className="table-td">{n.clienteNombre || "Consumidor final"}</td>
                <td className="table-td text-xs">{n.concepto}</td>
                <td className="table-td">{money(n.baseAjuste)}</td>
                <td className="table-td font-semibold">{money(n.valorTotal)}</td>
                <td className="table-td text-xs">{n.fecha}</td>
                <td className="table-td text-right">
                  <button
                    className="text-navy hover:underline text-xs font-semibold"
                    onClick={() => descargarArchivo(`/notas/${n.id}/documento`, `${n.numero}.xml`)}
                  >
                    XML
                  </button>
                </td>
              </tr>
            ))}
            {!cargando && notas.length === 0 && (
              <tr><td colSpan={9} className="table-td text-center text-textmuted py-6">Sin notas emitidas todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalNota && (
        <ModalNuevaNota
          inicial={modalNota}
          onClose={() => setModalNota(null)}
          onListo={() => { setModalNota(null); cargar(); }}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- nueva nota
const CONCEPTOS_FALLBACK: { credito: Concepto[]; debito: Concepto[] } = {
  credito: [
    { codigo: "devolucion", nombre: "Devolución de parte de los bienes o no aceptación del servicio" },
    { codigo: "anulacion", nombre: "Anulación de la factura o venta" },
    { codigo: "descuento", nombre: "Rebaja o descuento total o parcial" },
    { codigo: "ajuste_precio", nombre: "Ajuste de precio" },
    { codigo: "otros", nombre: "Otros" },
  ],
  debito: [
    { codigo: "intereses", nombre: "Intereses" },
    { codigo: "gastos_cobro", nombre: "Gastos por cobrar (cobranza, financieros)" },
    { codigo: "cambio_valor", nombre: "Cambio del valor" },
    { codigo: "otros", nombre: "Otros conceptos" },
  ],
};

function ModalNuevaNota({ inicial, onClose, onListo }: {
  inicial: { documentoOrigen?: string; documentoId?: number }; onClose: () => void; onListo: () => void;
}) {
  const { tienePermiso } = useAuth();
  const [tipo, setTipo] = useState<"credito" | "debito">("credito");
  const [documentoOrigen, setDocumentoOrigen] = useState(inicial.documentoOrigen || "factura_venta");
  const [documentoId, setDocumentoId] = useState<number | "">(inicial.documentoId || "");
  const [concepto, setConcepto] = useState("devolucion");
  const [motivo, setMotivo] = useState("");
  const [baseAjuste, setBaseAjuste] = useState(0);
  const [conceptos, setConceptos] = useState(CONCEPTOS_FALLBACK);
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [ventas, setVentas] = useState<VentaPos[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get("/notas/conceptos").then(setConceptos).catch(() => {});
    if (tienePermiso("comercial:ver")) {
      api.get("/comercial/facturas").then(setFacturas).catch(() => {});
    }
    if (tienePermiso("pos:ver")) {
      api.get("/pos/ventas").then((vs: VentaPos[]) => setVentas(vs.filter((v) => v.estado !== "anulada"))).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lista = tipo === "credito" ? conceptos.credito : conceptos.debito;
    if (!lista.some((c) => c.codigo === concepto)) setConcepto(lista[0]?.codigo || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, conceptos]);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentoId || baseAjuste <= 0) {
      setError("Seleccione el documento a ajustar e indique un valor base mayor a cero.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/notas", { tipo, documentoOrigen, documentoId: Number(documentoId), concepto, motivo, baseAjuste });
      onListo();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo emitir la nota.");
    } finally {
      setEnviando(false);
    }
  };

  const listaConceptos = tipo === "credito" ? conceptos.credito : conceptos.debito;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nueva nota crédito / débito</h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as "credito" | "debito")}>
              <option value="credito">Nota crédito</option>
              <option value="debito">Nota débito</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Documento origen</label>
            <select
              className="input-field" value={documentoOrigen}
              onChange={(e) => { setDocumentoOrigen(e.target.value); setDocumentoId(""); }}
              disabled={!!inicial.documentoOrigen}
            >
              <option value="factura_venta">Factura (Comercial)</option>
              <option value="venta_pos">Venta de mostrador (POS)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            {documentoOrigen === "factura_venta" ? "Factura a ajustar" : "Venta de mostrador a ajustar"}
          </label>
          {documentoOrigen === "factura_venta" ? (
            facturas.length > 0 ? (
              <select className="input-field" value={documentoId} onChange={(e) => setDocumentoId(e.target.value ? Number(e.target.value) : "")} disabled={!!inicial.documentoId}>
                <option value="">Seleccionar factura...</option>
                {facturas.map((f) => <option key={f.id} value={f.id}>{f.numero} — {f.clienteNombre} ({money(f.valorTotal)})</option>)}
              </select>
            ) : (
              <input className="input-field" type="number" placeholder="ID de la factura" value={documentoId}
                     onChange={(e) => setDocumentoId(e.target.value ? Number(e.target.value) : "")} disabled={!!inicial.documentoId} />
            )
          ) : (
            ventas.length > 0 ? (
              <select className="input-field" value={documentoId} onChange={(e) => setDocumentoId(e.target.value ? Number(e.target.value) : "")} disabled={!!inicial.documentoId}>
                <option value="">Seleccionar venta...</option>
                {ventas.map((v) => <option key={v.id} value={v.id}>{v.numero} — {v.clienteNombre} ({money(v.valorTotal)})</option>)}
              </select>
            ) : (
              <input className="input-field" type="number" placeholder="ID de la venta" value={documentoId}
                     onChange={(e) => setDocumentoId(e.target.value ? Number(e.target.value) : "")} disabled={!!inicial.documentoId} />
            )
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Concepto</label>
          <select className="input-field" value={concepto} onChange={(e) => setConcepto(e.target.value)}>
            {listaConceptos.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor base del ajuste (sin IVA)</label>
          <input className="input-field" type="number" min={0} step="100" value={baseAjuste} onChange={(e) => setBaseAjuste(Number(e.target.value))} required />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo (opcional)</label>
          <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Emitiendo..." : "Emitir nota"}</button>
        </div>
      </form>
    </div>
  );
}
